import { LocalDate } from '@lemon/core/model/common'
import { DateService } from '@lemon/core/service/date.service'
import { Asiakas, AsiakkaanKirjanpidonPeruste, KirjanpidonPeruste, LemonaidAsiakkaanKuukausihinta, LemonaidAsiakkaanMuuJatkuvaVeloitus, LemonaidAsiakkaanSopimuskausi } from '../model/kayttaja'
import { Laskuasetukset } from '../model/lasku'

type AsiakasType = Pick<Asiakas, 'kirjanpidonPerusteet'> & Partial<Pick<Asiakas, 'asiakasAvain'> & { avain: string; selvittelyLukituillaKkKaytossa: boolean }>
export class AsiakasJaettuLemonaidService {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  private _TAUKO_GRACE_PERIOD = 7 // days

  constructor(protected dateService: DateService) {}

  public annaKirjanpidonPeruste(asiakas: AsiakasType, date: LocalDate): KirjanpidonPeruste {
    if (asiakas.kirjanpidonPerusteet === undefined || asiakas.kirjanpidonPerusteet === null) {
      return KirjanpidonPeruste.MAKSU
    }
    for (const peruste of asiakas.kirjanpidonPerusteet) {
      if ((!peruste.alkaa || this.dateService.compareLocalDates(peruste.alkaa, '<=', date)) && (!peruste.loppuu || this.dateService.compareLocalDates(date, '<=', peruste.loppuu))) {
        return peruste.peruste
      }
    }
    throw new Error('Kirjanpidon perustetta ei löytynyt asiakkaalle ' + (asiakas.avain ?? asiakas.asiakasAvain))
  }

  public annaKirjanpidonPerusteObject(asiakas: AsiakasType, date: LocalDate): AsiakkaanKirjanpidonPeruste {
    if (asiakas.kirjanpidonPerusteet === undefined || asiakas.kirjanpidonPerusteet === null) {
      return null
    }
    for (const peruste of asiakas.kirjanpidonPerusteet) {
      if ((!peruste.alkaa || this.dateService.compareLocalDates(peruste.alkaa, '<=', date)) && (!peruste.loppuu || this.dateService.compareLocalDates(date, '<=', peruste.loppuu))) {
        return peruste
      }
    }
    throw new Error('Kirjanpidon perustetta ei löytynyt asiakkaalle ' + (asiakas.avain ?? asiakas.asiakasAvain))
  }

  public onkoKirjanpidonPerusteOllutVoimassaAikavalilla(asiakas: AsiakasType, alkaa: LocalDate, loppuu: LocalDate, peruste: KirjanpidonPeruste): boolean {
    if (asiakas.kirjanpidonPerusteet === undefined || asiakas.kirjanpidonPerusteet === null) {
      return peruste === KirjanpidonPeruste.MAKSU
    }
    for (const p of asiakas.kirjanpidonPerusteet) {
      if (
        peruste === p.peruste &&
        // See https://stackoverflow.com/a/325964/7740038 for an explanation
        (!p.loppuu || this.dateService.compareLocalDates(alkaa, '<=', p.loppuu)) &&
        (!p.alkaa || this.dateService.compareLocalDates(loppuu, '>=', p.alkaa))
      ) {
        return true
      }
    }
    return false
  }

  annaKausiPaivamaaralle(kaudet: LemonaidAsiakkaanSopimuskausi[], pvm: LocalDate): LemonaidAsiakkaanSopimuskausi {
    if (!kaudet) {
      return null
    }
    for (const kausi of kaudet) {
      if (kausi.loppuu) {
        if (this.dateService.compareLocalDates(kausi.alkaa, '<=', pvm) && this.dateService.compareLocalDates(kausi.loppuu, '>=', pvm)) {
          return kausi
        }
      } else if (this.dateService.compareLocalDates(kausi.alkaa, '<=', pvm)) {
        return kausi
      }
    }
    return null
  }

  annaNykyinenTaiVoimaanTulevaSopimuskausi(kaudet: LemonaidAsiakkaanSopimuskausi[]): LemonaidAsiakkaanSopimuskausi {
    if (!kaudet) {
      return null
    }
    const currentLocalDate = this.dateService.currentLocalDate()
    const nykyinen = this.annaKausiPaivamaaralle(kaudet, currentLocalDate)
    if (nykyinen) {
      return nykyinen
    }
    const tuleva = [...kaudet]
      .sort((a, b) => this.dateService.localDateToNumber(a.alkaa) - this.dateService.localDateToNumber(b.alkaa))
      .find(k => this.dateService.compareLocalDates(k.alkaa, '>', currentLocalDate))
    if (tuleva) {
      return tuleva
    }
    return null
  }

  public onkoSopimuskausiTauolla(sopimuskausi: LemonaidAsiakkaanSopimuskausi, localDate: LocalDate): boolean {
    if (sopimuskausi && sopimuskausi.tauot) {
      for (const tauko of sopimuskausi.tauot) {
        if (tauko.tyyppi === 'tauko' && this.dateService.onkoLocalDateKahdenValissa(localDate, tauko.alkaa, tauko.loppuu ?? localDate)) {
          return true
        }
      }
    }
    return false
  }

  public onkoSopimuskausiPoytalaatikossa(sopimuskausi: LemonaidAsiakkaanSopimuskausi, localDate: LocalDate): boolean {
    if (sopimuskausi && sopimuskausi.tauot) {
      for (const tauko of sopimuskausi.tauot) {
        if (tauko.tyyppi === 'poytalaatikko' && this.dateService.onkoLocalDateKahdenValissa(localDate, tauko.alkaa, tauko.loppuu ?? localDate)) {
          return true
        }
      }
    }
    return false
  }

  public onkoKirjanpidonPerusteSuoriteTaiLaskuperusteinen(asiakas: AsiakasType, date: LocalDate): boolean {
    const peruste = this.annaKirjanpidonPeruste(asiakas, date)
    return peruste === KirjanpidonPeruste.SUORITE || peruste === KirjanpidonPeruste.LASKU
  }

  public onkoSelvittelyLukituillaKaytossa(asiakas: AsiakasType): boolean {
    return !!asiakas.selvittelyLukituillaKkKaytossa
  }

  public annaVoimassaOlevaReskontranTyyppi(asiakas: AsiakasType, laskuasetukset: Laskuasetukset): 'kirjanpitoreskontra' | 'laskureskontra' | null {
    const currentDate = this.dateService.currentLocalDate()
    const peruste = this.annaKirjanpidonPerusteObject(asiakas, currentDate)

    if (
      (peruste?.peruste === KirjanpidonPeruste.SUORITE || peruste?.peruste === KirjanpidonPeruste.LASKU) &&
      peruste.kirjanpitoReskontraAlkaen &&
      this.dateService.compareLocalDates(peruste.kirjanpitoReskontraAlkaen, '>=', currentDate)
    ) {
      return 'kirjanpitoreskontra'
    }

    if (laskuasetukset?.maksupReskontraActive) {
      return 'laskureskontra'
    }

    return null
  }

  annaNykyisetMuutJatkuvatVeloitukset(veloitukset: LemonaidAsiakkaanMuuJatkuvaVeloitus[], paiva: LocalDate): LemonaidAsiakkaanMuuJatkuvaVeloitus[] {
    return (veloitukset || []).filter(
      v => !v.poistettu && this.dateService.compareLocalDates(v.voimassaAlkaen, '<=', paiva) && (!v.paattyy || this.dateService.compareLocalDates(v.paattyy, '>', paiva)),
    )
  }

  annaNykyisetJaVoimaanTulevatMuutJatkuvatVeloitukset(
    veloitukset: LemonaidAsiakkaanMuuJatkuvaVeloitus[],
    paiva: LocalDate,
  ): { nykyiset: LemonaidAsiakkaanMuuJatkuvaVeloitus[]; tulevat: LemonaidAsiakkaanMuuJatkuvaVeloitus[] } {
    const notDeleted = (veloitukset || []).filter(v => !v.poistettu && (!v.paattyy || this.dateService.compareLocalDates(v.paattyy, '>', paiva)))
    return {
      nykyiset: notDeleted.filter(v => this.dateService.compareLocalDates(v.voimassaAlkaen, '<=', paiva)),
      tulevat: notDeleted.filter(v => this.dateService.compareLocalDates(v.voimassaAlkaen, '>', paiva)),
    }
  }

  annaNykyinenJaSeuraavaLemonaidHinta(hinnat: LemonaidAsiakkaanKuukausihinta[]) {
    if (!hinnat?.length) {
      return { nykyinen: null, seuraava: null }
    }
    const currentNumberDate = this.dateService.currentNumberDate()
    const sorted = [...hinnat].sort((a, b) => a.voimassaAlkaen - b.voimassaAlkaen)
    const nykyisenIndeksi = sorted.findIndex(hinta => hinta.voimassaAlkaen <= currentNumberDate && hinta.voimassaLoppuen >= currentNumberDate)

    if (nykyisenIndeksi > -1) {
      const nykyinen = sorted[nykyisenIndeksi]
      const seuraava = nykyisenIndeksi < sorted.length - 1 ? sorted[nykyisenIndeksi + 1] : null
      return { nykyinen: nykyinen, seuraava: seuraava }
    }
    const seuraavaTulevaHinta = sorted.find(hinta => hinta.voimassaAlkaen >= currentNumberDate)
    if (seuraavaTulevaHinta) {
      return { nykyinen: null, seuraava: seuraavaTulevaHinta }
    }

    // No prices found
    return { nykyinen: null, seuraava: null }
  }
}
